import { Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";
import {
  Stepper,
  StepperItem,
  StepperTitle,
  StepperSeparator,
} from "@/components/ui/Stepper";

// --- 类型定义 ---
export type ProcessStepStatus = "pending" | "running" | "success";

// 详细的工具/子步骤数据结构
export interface AiSubStep {
  id: string;
  title: string;
  status: ProcessStepStatus;
}

// 组件 Props
export interface AiThinkRenderProps {
  toolSteps: AiSubStep[];
  currentGlobalStep: 0 | 1 | 2 | 3 | 4;
  className?: string;
  thinkContent?: string | null;
  isThinking?: boolean;
  thinkingComplete?: boolean;
}

export function AiThinkRender({
  toolSteps,
  currentGlobalStep,
  className,
  thinkContent,
  isThinking,
  thinkingComplete,
}: AiThinkRenderProps) {
  const mainSteps = [
    {
      id: "analysis",
      titleRunning: "问题分析中",
      titleDone: "问题分析完成",
      logicalIndex: 0,
    },
    {
      id: "tools",
      titleRunning: "工具调用中",
      titleDone: "工具调用完成",
      logicalIndex: 1,
    },
    {
      id: "thinking",
      titleRunning: "思考中",
      titleDone: "思考完成",
      logicalIndex: 2,
    },
    {
      id: "generation",
      titleRunning: "答案生成中",
      titleDone: "答案生成完成",
      logicalIndex: 3,
    },
  ] as const;

  const filteredSteps = mainSteps.filter((step) => {
    if (step.id === "tools" && toolSteps.length === 0) {
      return false;
    }
    if (step.id === "thinking" && !isThinking) {
      return false;
    }
    return true;
  });

  const activeStepIndex = filteredSteps.findIndex(
    (step) => step.logicalIndex === currentGlobalStep
  );

  let stepperValue = 0;
  if (currentGlobalStep >= 4) {
    stepperValue = filteredSteps.length + 1;
  } else if (activeStepIndex !== -1) {
    stepperValue = activeStepIndex + 1;
  } else {
    const nextStepIndex = filteredSteps.findIndex(
      (step) => step.logicalIndex > currentGlobalStep
    );
    if (nextStepIndex !== -1) {
      stepperValue = nextStepIndex + 1;
    } else {
      stepperValue = filteredSteps.length + 1;
    }
  }

  return (
    <div className={cn("w-full font-sans", className)}>
      {/* 步骤列表 */}
      <Stepper
        orientation="vertical"
        value={stepperValue}
        className="flex flex-col gap-0"
      >
        {filteredSteps.map((step, index) => {
          const isLast = index === filteredSteps.length - 1;
          const logicalIndex = step.logicalIndex;
          let status: ProcessStepStatus = "pending";

          if (currentGlobalStep > logicalIndex) status = "success";
          else if (currentGlobalStep === logicalIndex) status = "running";

          const displayTitle =
            status === "success" ? step.titleDone : step.titleRunning;

          return (
            <StepperItem
              key={step.id}
              step={index + 1}
              loading={true}
              className={cn(
                "relative block overflow-visible pl-8",
                step.id === "tools" ? "pb-2" : "pb-6"
              )}
            >
              {!isLast && (
                <StepperSeparator
                  className={cn(
                    "absolute bottom-0 left-[11px] top-7 m-0 h-auto w-[2px] rounded-none bg-[#f2f2f7] group-data-[orientation=vertical]/stepper-nav:h-auto group-data-[orientation=vertical]/stepper-nav:w-[2px]",
                    "data-[state=completed]:bg-[#007aff]"
                  )}
                />
              )}

              <div
                className={cn(
                  "absolute left-0 top-0.5 z-10 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-white",
                  status === "success" && "bg-[#007aff]",
                  status === "running" && "bg-white",
                  status === "pending" && "border-2 border-[#e5e5ea] bg-white"
                )}
              >
                {status === "running" && (
                  <Loader2 className="h-6 w-6 animate-spin text-[#007aff]" />
                )}
              </div>

              {/* 内容区域 */}
              <div className="flex flex-col pt-2">
                <StepperTitle
                  className={cn(
                    "text-sm font-medium leading-none transition-colors",
                    status === "pending" ? "text-gray-400" : "text-gray-900"
                  )}
                >
                  {displayTitle}
                </StepperTitle>

                {step.id === "tools" &&
                  (status === "running" || status === "success") && (
                    <div className="mt-3">
                      <ul className="space-y-2">
                        <AnimatePresence initial={false}>
                          {toolSteps.map((subStep) => (
                            <motion.li
                              key={subStep.id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              className="flex items-center gap-2 text-xs"
                            >
                              {/* 子项状态图标 */}
                              {subStep.status === "running" && (
                                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-[#007aff]" />
                              )}
                              {subStep.status === "success" && (
                                <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#007aff]" />
                              )}

                              {/* 子项文本 */}
                              <span
                                className={cn(
                                  "font-mono",
                                  subStep.status === "running"
                                    ? "font-medium text-gray-800"
                                    : "text-gray-500",
                                  subStep.status === "running" &&
                                    "after:ml-1 after:inline-block after:h-3 after:w-1 after:align-middle after:bg-[#007aff] after:content-[''] after:animate-pulse"
                                )}
                              >
                                {subStep.title}
                              </span>
                            </motion.li>
                          ))}
                        </AnimatePresence>
                      </ul>
                    </div>
                  )}
                {step.id === "thinking" &&
                  isThinking &&
                  (status === "running" || status === "success") && (
                    <div className="mb-1 mt-3">
                      <div className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">
                        {thinkContent ? (
                          <>
                            {thinkContent}
                            {!thinkingComplete && (
                              <span className="inline-block ml-1 w-1 h-3 bg-[#007aff] animate-pulse" />
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">思考中...</span>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </StepperItem>
          );
        })}
      </Stepper>
    </div>
  );
}
